<script setup lang="ts">
/**
 * TODO: make this look more "native", e.g. sidebar
 * TODO: make create button look like a bigger version of the save button
 */
const { state: sharedFolderState, createSharedFolder } = useSharedFolders()
const { sharedFolderCreationTestId } = useTestId()

const handleInput = ref<HTMLFormElement | null>(null)

const valid = computed(() => {
  const { name, handle } = sharedFolderState.value.new
  if (!name)
    return false
  if (handle.length < 3 || handle.length > 128)
    return false
  return handleInput.value?.checkValidity()
})

function onCreateButtonPressed() {
  if (!valid.value)
    return
  createSharedFolder()
}

function sanitizeSharedFolderHandle() {
  let { handle } = sharedFolderState.value.new
  handle = handle
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\-_~.]/g, "-")
    .replace(/-[-_]+/g, "-") // double hyphens to single hyphen
    .replace(/_[-_]+/g, "_") // double underscores to single underscore
    .replace(/^[-_]/, "") // remove leading hyphen/underscore
    .slice(0, 128)
  sharedFolderState.value.new.handle = handle
}
</script>

<template>
  <dropdown-menu class="create-shared-folder-container" :data-testid="sharedFolderCreationTestId">
    <menu-section>
      <div class="header">
        <div class="title">
          Create a Shared Folder
        </div>
        <div class="description">
          Shared folders let you collaborate on your files in a private space with granular per-individual permissions.
        </div>
      </div>
    </menu-section>
    <menu-separator />
    <menu-section>
      <form
        ref="handleInput"
        class="create-shared-folder-form"
        @submit.prevent
      >
        <div class="input">
          <div class="label">
            Name
          </div>
          <interface-input
            v-model.trim="sharedFolderState.new.name"
            autofocus
            placeholder="Enter the folder's name"
          />
        </div>
        <div class="input">
          <div class="label">
            Handle
          </div>
          <interface-input
            v-model.trim="sharedFolderState.new.handle"
            pattern="[a-z0-9]+([\-_\~\.][a-z0-9]+)*"
            minlength="3"
            leading
            style-invalid
            placeholder="Enter a unique handle (lower case only)"
            @keydown.enter.prevent="onCreateButtonPressed()"
            @input="sanitizeSharedFolderHandle()"
          >
            <template #leading>
              <span class="">@</span>
            </template>
          </interface-input>
        </div>
      </form>
    </menu-section>

    <menu-separator />
    <menu-section>
      <div class="flex justify-end">
        <div class="relative inline-block text-left">
          <div
            class="creation-inline-select-button"
            :class="{ valid }"
            @click="onCreateButtonPressed()"
          >
            <div>
              Create
            </div>
            <interface-spinner v-if="sharedFolderState.new.loading" class="action spinner" />
          </div>
        </div>
      </div>
    </menu-section>
    <!-- <div
      class="create-button"
      :class="{ valid }"
      @click="onCreateButtonPressed()"
    >
      <interface-spinner
        v-if="sharedFolderState.new.loading"
        class="h-4 w-4"
      />
      <div>Create this shared folder</div>
    </div> -->
  </dropdown-menu>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.create-shared-folder-container
  @apply w-80
  .header
    @apply space-y-1
    .title
      @apply t-menu-lg mb-2 text-[#{$global-t-default}]
    .description
      @apply text-sm text-$global-t-secondary

.create-shared-folder-form
  @apply flex flex-col gap-3
  .input
    .label
      @apply t-menu-sm mb-1

.creation-inline-select-button
  @apply flex items-center gap-1 rounded-[#{$menu-br-item}] p-1 px-1.5 h-6 t-menu-sm outline rounded-[#{$menu-br-item}] outline-0.5
  &:not(.valid)
    @apply outline-[#{$menu-b-input-hover}] text-[#{$global-t-tertiary}] cursor-not-allowed
  &.valid
    @apply outline-$global-blue-secondary bg-$global-blue-accent text-$global-blue-primary cursor-pointer
    &:hover
      @apply bg-[#{$menu-bg-item-hover}]
  .action
    @apply w-3 h-3
</style>
